'use client';

import type { JSX } from "react";

import { useState } from 'react';

import type { TabsBlock } from '../types';

import { DescriptionBlocks } from '../DescriptionBlocks';
import styles from './Tabs.module.css';

interface TabsProps {
  block: TabsBlock;
  blockId: string;
}

export function Tabs({ block, blockId }: TabsProps): JSX.Element {
  const [activeId, setActiveId] = useState(block.items[0]?.id);
  if (block.items.length === 0) return <></>;

  const isStacked = block.sourcePattern === 'bootstrapStacked';
  const isIndustryLinkList = block.variant === 'industryLinkList';

  return (
    <div
      className={`${styles.wrapper} ${isStacked ? styles.stacked : ''} ${isIndustryLinkList ? styles.industryLinkList : ''}`}
      data-cms-block-id={blockId}
      data-cms-field="tabs"
      data-source-pattern={block.sourcePattern ?? 'bootstrap'}
    >
      <div role="tablist" className={styles.tablist} aria-label="Section tabs">
        {block.items.map((item) => {
          const isActive = item.id === activeId;
          return (
            // Rendered as an anchor so source bootstrap tab toggles
            // (`<a href="#panelId" data-toggle="tab">`) keep their fragment
            // href + `<id>-tab` id for in-page navigation and parity.
            <a
              key={item.id}
              href={`#${item.id}`}
              role="tab"
              id={`${item.id}-tab`}
              aria-selected={isActive}
              aria-controls={item.id}
              tabIndex={isActive ? 0 : -1}
              className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
              onClick={(event) => {
                event.preventDefault();
                setActiveId(item.id);
              }}
            >
              {item.label}
            </a>
          );
        })}
      </div>
      {block.items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <section
            key={item.id}
            role="tabpanel"
            id={item.id}
            aria-labelledby={`${item.id}-tab`}
            hidden={!isStacked && !isActive}
            className={styles.panel}
          >
            <DescriptionBlocks
              blocks={item.blocks}
              blockIdPrefix={`${blockId}.tabs.${item.id}`}
            />
          </section>
        );
      })}
    </div>
  );
}
